import React, {useContext, useEffect, useState} from 'react';
import '../../assets/css/Dentobook.css';
import '../../assets/css/Clinic.css';
import {Button, Col, Form, Input, Modal, Row, Select, Space, Spin, Table} from 'antd';
import {CaretDownOutlined, DeleteOutlined, EditOutlined} from '@ant-design/icons';
import {ToothConditionCode, StatusResponse, StatusCreate, StatusUpdate} from "../../api-client";
import {accessibilityRoles} from "../../utils/accessUtils";
import ClinicSearchForm from "./ClinicSearchFrom";
import AccessControl from "../common/AccessControl";
import {FormattedMessage, useIntl} from "react-intl";
import {isMobileOnly} from "react-device-detect";
// @ts-ignore
import {v4 as uuidv4} from 'uuid';
import {useForm} from "antd/es/form/Form";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {getStatusesApi, getNZIZApi} from "../../api/apiInstances";
import logger from "../../utils/logger";

const Status = () => {
    const intl = useIntl();
    const [form] = useForm();
    const {locale, userOrganization} = useContext(DentobookApplicationContext);
    const {Option} = Select;
    const [newStatusName, setNewStatusName] = useState<string>('');
    const [newStatusLabel, setNewStatusLabel] = useState<string>('');
    const [newStatusColor, setNewStatusColor] = useState<string>('');
    const [newNzisCondition, setNewNzisCondition] = useState<string>('');
    const [editStatusId, setEditStatusId] = useState<string>('');
    const [editMode, setEditMode] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [statuses, setStatuses] = useState<StatusResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [keyword, setKeyword] = useState<string>('');
    const [isAddStatusModalVisible, setIsAddStatusModalVisible] = useState<boolean>(false);
    const [displayedStatuses, setDisplayedStatuses] = useState<StatusResponse[]>([]);
    const [nzisToothConditionCodes, setNzisToothConditionCodes] = useState<ToothConditionCode[]>([]);

    const statusesApi = getStatusesApi();
    const nzisApi = getNZIZApi();
    const showAddModal = () => setIsAddStatusModalVisible(true);
    const showEditModal = (status: StatusResponse) => {
        if (status) {
            form.setFieldsValue({
                condition: status.displayName,  // Backend provides locale-resolved name
                nzisCondition: status.nzisCondition
            });
            setEditStatusId(status.id ?? '');
            setNewStatusName(status.displayName ?? '');  // Use displayName
            setNewStatusLabel(status.label ?? '');
            setNewStatusColor(status.color ?? '');
            setNewNzisCondition(status.nzisCondition ?? '')
            setEditMode(true);
        }
    };

    useEffect(() => {
        loadData();
    }, []);
    const loadData = () => {

        statusesApi.fetchAllStatuses()
            .then(response => {
                // Sort by displayName (backend provides locale-resolved name)
                const compareFunc = (statusOne: StatusResponse, statusTwo: StatusResponse) =>
                    (statusOne.displayName || '').localeCompare(statusTwo.displayName || '');
                setStatuses(response.data.sort(compareFunc));
                setDisplayedStatuses(response.data);
                nzisApi.fetchToothConditionCodes()
                    .then(res => {
                        setNzisToothConditionCodes(res.data);
                        setLoading(false);
                    })
                    .catch(err => {
                        logger.log(err);
                        setLoading(false);
                    })
            })
            .catch(error => {
                logger.error("Failed to load statuses:", error);
                setLoading(false);
            });
    };

    useEffect(() => {

        if (keyword) {
            // Filter by displayName (backend provides locale-resolved name)
            const filteredStatuses = statuses.filter(status => status.displayName?.toLowerCase().includes(keyword.toLowerCase()));
            setDisplayedStatuses(filteredStatuses);
        } else {
            setDisplayedStatuses(statuses);
        }
    }, [statuses, keyword]);

    const onAddButtonClick = () => {
        showAddModal();
    };

    const showDeleteModal = (status: StatusResponse) => {
        if (status) {
            setEditStatusId(status.id ?? '');
            setNewStatusName(status.name ?? '');
            setNewStatusLabel(status.label ?? '');
            setNewStatusColor(status.color ?? '');
            setNewNzisCondition(status.nzisCondition ?? '')
        }
        setIsDeleteModalVisible(true);
    };

    const resetState = () => {
        setEditStatusId('');
        setNewStatusName('');
        setNewStatusLabel('');
        setNewNzisCondition('')
        form.setFieldsValue({
            condition: '',
            nzisCondition: ''
        });
        setEditMode(false);
        setIsDeleteModalVisible(false);
        setIsAddStatusModalVisible(false);
    };

    const closeAddStatusModal = () => {
        setIsAddStatusModalVisible(false);
    };

    const handleCancel = () => {
        closeAddStatusModal();
        // Reset state
        setEditStatusId('');
        setNewStatusName('');
        setNewStatusLabel('');
        setNewStatusColor('');
        setNewNzisCondition('')
        form.setFieldsValue({
            condition: '',
            nzisCondition: ''
        });
        setEditMode(false);
    };

    const handleAddOk = async () => {
        try {
            await form.validateFields();
            handleCreateStatus();
            closeAddStatusModal();
        } catch (err) {
            logger.log(err);
        }
    };

    const handleCreateStatus = async () => {
        if (!userOrganization?.id) return;

        const statusToCreate: StatusCreate = {
            name: locale === 'bg' ? newStatusName : undefined,  // Use BCP-47 lowercase
            nameEn: locale !== 'bg' ? newStatusName : undefined,
            label: newStatusLabel,
            color: newStatusColor,
            nzisCondition: newNzisCondition,
            organizationId: userOrganization.id
        };
        try {
            await statusesApi.createStatus(statusToCreate);
            loadData();
        } catch (error) {
            logger.error("Failed to create Status:", error);
        }
        resetState();
    };

    const handleEditStatusOk = async () => {
        try {
            await form.validateFields();

            const statusUpdate: StatusUpdate = {
                name: locale === 'BG' ? newStatusName : undefined,
                nameEn: locale !== 'BG' ? newStatusName : undefined,
                label: newStatusLabel,
                color: newStatusColor,
                nzisCondition: newNzisCondition
            };
            try {
                if (editStatusId != null) {
                    await statusesApi.updateStatus(editStatusId, statusUpdate);
                }
                loadData();
            } catch (error) {
                logger.error("Failed to create status:", error);
            }
            resetState();
        } catch (err) {
            logger.log(err);
        }
    };

    const handleEditStatusCancel = () => {
        resetState();
    };

    const handleDeleteStatusOk = async () => {
        logger.log(editStatusId + "DELETE")
        try {
            if (editStatusId != null) {
                await statusesApi.deleteStatus(editStatusId);
            }
            loadData();
        } catch (error) {
            logger.error("Failed to delete status:", error);
        }
        resetState();
    };

    const handleDeleteStatusCancel = () => {
        resetState();
    };

    const modalContent = <div className="medical-history-modal-group">
        <Form form={form} layout="vertical">
            <Form.Item
                name="condition"
                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                className={"dentobook-form-item dentobook-simple-input"}
                label={<FormattedMessage id="naming"/>}>
                <Input
                    value={newStatusName}
                    onChange={(e) => setNewStatusName(e.target.value)}
                />
            </Form.Item>
            {/* NZIS/NHIS only for Bulgarian organizations */}
            {userOrganization?.country === 'BG' && (
                <Form.Item
                    name="nzisCondition"
                    rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                    className={"dentobook-form-item dentobook-simple-input"}
                    label={<FormattedMessage id="nzis_condition"/>}>
                    <Select
                        suffixIcon={<CaretDownOutlined/>}
                        size={"large"}
                        className="dentobook-input"
                        value={newNzisCondition}
                        onChange={(value) => setNewNzisCondition(value)}
                    >
                        {nzisToothConditionCodes.sort((statusOne, statuseTwo) => statusOne.descriptionBG?.localeCompare(statuseTwo.descriptionBG!)!)
                            .map(status => (
                                <Option key={status.key}
                                        value={status.key}>{status.descriptionBG}</Option>
                            ))}
                    </Select>
                </Form.Item>
            )}
            <Row gutter={24}>
                <Col flex={2.5}>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="abbreviation"/>}
                    >
                        <Input
                            value={newStatusLabel}
                            onChange={(e) => setNewStatusLabel(e.target.value)}
                        />
                    </Form.Item>
                </Col>
                <Col>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="color"/>}
                    >
                        <Select
                            suffixIcon={<CaretDownOutlined/>}
                            style={!isMobileOnly ? {width: 300} : {width: '90vw'}}
                            size={"large"}
                            className="dentobook-input"
                            value={newStatusColor}
                            onChange={(value) => setNewStatusColor(value)}
                        >
                            {
                                getAllHexColors().map(color => (
                                        <Option key={uuidv4()} value={color}>
                                            <Space>
                                                <div
                                                    style={{
                                                        backgroundColor: color,
                                                        borderRadius: '10px',
                                                        width: '20px',
                                                        height: '20px',
                                                    }}
                                                ></div>
                                                <span>{color}</span>
                                            </Space>
                                        </Option>
                                    )
                                )}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    </div>;

    return (
        <AccessControl
            accessibleFor={[accessibilityRoles.CLINIC_ADMIN, accessibilityRoles.MEDICAL_DOCTOR, accessibilityRoles.MEDICAL_ASSISTANT, accessibilityRoles.RECEPTIONIST, accessibilityRoles.DENTAL_TECHNICIAN]}>
            <Col>
                <ClinicSearchForm
                    keyword={keyword}
                    setKeyword={setKeyword}
                    loadData={loadData}
                    onAddButtonClick={onAddButtonClick}
                />
            </Col>
            {loading ? (
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <Spin tip={<FormattedMessage id="loading" />} />
                </div>
            ) : (
            <Table dataSource={displayedStatuses}
                   pagination={false}
                   style={{overflowX: 'auto'}}
                   className="dentobook-table">
                <Table.Column key="name"
                              className={'custom-column-style'}
                              sorter={(statusOne: StatusResponse, statusTwo: StatusResponse) =>
                                  (statusOne.displayName || '').localeCompare(statusTwo.displayName || '')}
                              title={<FormattedMessage id="conditions"/>}
                              render={(status) => <span className="font-weight-500">{status.displayName}</span>}
                />
                <Table.Column key="id"
                              sorter={(statusOne: StatusResponse, statusTwo: StatusResponse) => statusOne.label!.localeCompare(statusTwo.label!)}
                              className={'custom-column-style'}
                              title={<FormattedMessage id="abbreviation"/>}
                              render={(status) => <span className="font-weight-500">{status.label}</span>}
                />
                <Table.Column key="color"
                              sorter={(statusOne: StatusResponse, statusTwo: StatusResponse) => statusOne.color!.localeCompare(statusTwo.color!)}
                              className={'custom-column-style'}
                              title={<FormattedMessage id="color"/>}
                              render={(status) => (
                                  <div style={{display: 'flex', alignItems: 'center'}}>
                                      <div
                                          style={{
                                              backgroundColor: getHexColor(status.color),
                                              borderRadius: '10px',
                                              width: '20px',
                                              height: '20px',
                                              marginRight: '10px',
                                          }}
                                      ></div>
                                      <span>{getHexColor(status.color)}</span>
                                  </div>
                              )}

                />
                <Table.Column key="actions"
                              title={<div></div>}
                              className={'custom-column-style'}
                              render={(status) => {
                                  return (
                                      <div style={{display: 'flex', flexDirection: 'row', alignItems: 'right'}}>
                                          <Button className="text-black font-20 ml-1" type="link"
                                                  icon={<EditOutlined/>}
                                                  onClick={() => showEditModal(status)}
                                          />

                                          <Button className="text-black font-20" type="link"
                                                  icon={<DeleteOutlined/>}
                                                  onClick={() => showDeleteModal(status)}/>
                                      </div>
                                  );
                              }}
                />
            </Table>
            )}
            {isMobileOnly ? <div style={{marginBottom: 150}}></div> : null}
            <Modal
                transitionName=""
                okButtonProps={{
                    className: "modal-btn"
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                title={<FormattedMessage id="addStatus"/>}
                okText={intl.formatMessage({id: 'save'})}
                cancelText={intl.formatMessage({id: 'cancel'})}
                open={isAddStatusModalVisible}
                onOk={handleAddOk}
                onCancel={handleCancel}
                className="dentobook-modal"
            >
                {modalContent}
            </Modal>
            <Modal
                transitionName=""
                okButtonProps={{
                    className: "modal-btn"
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                title={<FormattedMessage id="editStatus"/>}
                okText={intl.formatMessage({id: 'save'})}
                cancelText={intl.formatMessage({id: 'cancel'})}
                open={editMode}
                onOk={handleEditStatusOk}
                onCancel={handleEditStatusCancel}
                className="dentobook-modal"
            >
                {modalContent}
            </Modal>
            <Modal
                transitionName=""
                okButtonProps={{
                    className: "modal-btn",
                    danger: true,
                    type: "primary",
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                className="dentobook-modal"
                title={<FormattedMessage id="deleteStatus"/>}
                okText={intl.formatMessage({id: 'delete'})}
                cancelText={intl.formatMessage({id: 'cancel'})}
                open={isDeleteModalVisible} onOk={handleDeleteStatusOk}
                onCancel={handleDeleteStatusCancel}
            >
                <p><FormattedMessage id="statusDeleteConfirmation"/></p>
            </Modal>
        </AccessControl>

    );
};
export default Status;

const colorToHexMap: { [key: string]: string } = {
    red: "#FF0000",
    lightpurple: "#D8BFD8",
    lightblue: "#ADD8E6",
    grey: "#808080",
    darkgrey: "#A9A9A9",
    black: "#000000",
    purple: "#800080",
    orange: "#FFA500",
    green: "#a3ffb4"
};

function getAllHexColors(): string[] {
    return Array.from(new Set(Object.values(colorToHexMap)));
}

function getHexColor(colorName: string | undefined): string {
    const hexColorPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    if (!colorName) {
        return '';
    }

    if (hexColorPattern.test(colorName)) {
        return colorName;
    }

    return colorToHexMap[colorName] || colorName;
}


